import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import PayPalButton from "@/components/PayPalButton";
import StripePaymentForm from "@/components/StripePaymentForm";
import SepaPaymentForm from "@/components/SepaPaymentForm";
import { useToast } from "@/hooks/use-toast";
import { Loader2, LogOut, ChevronRight, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const STEPS = ["Dossier", "Paiement", "Confirmation"];

const MIN_CUSTOM_PAYMENT_AMOUNT = 50;

const eur = (v) =>
  (v ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const PaymentPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [member, setMember] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("stripe");

  // CB — étape de choix du montant avant de créer l'intent Stripe (l'intent
  // n'est créé qu'à la confirmation, pas à l'ouverture de l'onglet).
  const [cbStep, setCbStep] = useState("choose"); // "choose" | "pay"
  const [cbAmountMode, setCbAmountMode] = useState("remaining"); // "remaining" | "custom"
  const [cbCustomAmount, setCbCustomAmount] = useState("");
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeAmount, setStripeAmount] = useState(null);

  // SEPA mensuel = acompte carte (imposé par l'association) puis mandat SEPA sur
  // le solde restant, en 12 mensualités — aligné sur le lien de paiement admin.
  const [sepaClientSecret, setSepaClientSecret] = useState(null); // SetupIntent (mandat)
  const [sepaMonthlyAmount, setSepaMonthlyAmount] = useState(null);
  const [sepaRemainingAmount, setSepaRemainingAmount] = useState(null); // base étalée (hors acompte)
  const [sepaLoading, setSepaLoading] = useState(false);
  const [sepaSetupError, setSepaSetupError] = useState(null);
  const [sepaResult, setSepaResult] = useState(null);

  const [sepaStep, setSepaStep] = useState("deposit"); // "deposit" | "mandate"
  const [sepaDepositAmount, setSepaDepositAmount] = useState(null);
  const [sepaDepositWaived, setSepaDepositWaived] = useState(false);
  const [sepaDepositPaymentId, setSepaDepositPaymentId] = useState(null);
  const [sepaDepositSecret, setSepaDepositSecret] = useState(null); // PaymentIntent carte de l'acompte
  const [sepaDepositLoading, setSepaDepositLoading] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("hafani_member_token");
    navigate("/login");
  };

  const fetchMemberAndBalance = useCallback(async () => {
    try {
      const token = localStorage.getItem("hafani_member_token");
      if (!token) { navigate("/login"); return; }
      const [memberRes, balanceRes] = await Promise.all([
        axios.get(`${API}/members/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/payments/my-balance`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setMember(memberRes.data);
      setBalance(balanceRes.data);
    } catch {
      toast({ title: "Erreur", description: "Impossible de charger vos informations.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  useEffect(() => { fetchMemberAndBalance(); }, [fetchMemberAndBalance]);

  const remaining = balance?.remaining_amount ?? member?.cotisation_amount ?? 50;
  const isSettled = balance ? balance.is_settled : false;
  // En dessous du solde restant, un montant libre doit atteindre 50 € — sauf
  // si le solde restant est lui-même inférieur (dans ce cas seul le règlement
  // intégral est possible, quel que soit son montant).
  const customMinAmount = Math.min(MIN_CUSTOM_PAYMENT_AMOUNT, remaining);

  // Crée le PaymentIntent Stripe seulement à la confirmation du montant choisi.
  const handleCbContinue = async () => {
    const amount = cbAmountMode === "remaining" ? remaining : parseFloat(cbCustomAmount);
    if (!amount || Number.isNaN(amount) || amount < customMinAmount || amount > remaining) return;

    setStripeLoading(true);
    try {
      const token = localStorage.getItem("hafani_member_token");
      const res = await axios.post(
        `${API}/payments/stripe/create-intent`,
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStripeClientSecret(res.data.client_secret);
      setStripeAmount(res.data.amount);
      setCbStep("pay");
    } catch (err) {
      toast({
        title: "Erreur",
        description: err?.response?.data?.detail || "Impossible d'initialiser le paiement Stripe.",
        variant: "destructive",
      });
    } finally {
      setStripeLoading(false);
    }
  };

  const handleCbChangeAmount = () => {
    setCbStep("choose");
    setStripeClientSecret(null);
    setStripeAmount(null);
  };

  // Ouverture de l'onglet SEPA : prépare le mandat (SetupIntent, non facturé) et
  // détermine l'acompte / la mensualité. Reprend directement à l'étape mandat si
  // un acompte a déjà été encaissé sans mandat signé.
  useEffect(() => {
    if (provider !== "sepa" || sepaClientSecret || sepaSetupError || !member || isSettled) return;
    const fetchSepaSetup = async () => {
      setSepaLoading(true);
      try {
        const token = localStorage.getItem("hafani_member_token");
        const res = await axios.post(
          `${API}/payments/stripe/create-sepa-setup`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSepaClientSecret(res.data.client_secret);
        setSepaMonthlyAmount(res.data.monthly_amount);
        setSepaRemainingAmount(res.data.base_amount);
        setSepaDepositAmount(res.data.deposit_amount);
        setSepaDepositWaived(!!res.data.deposit_waived);
        if (res.data.deposit_paid) {
          setSepaDepositPaymentId(res.data.deposit_payment_id);
          setSepaStep("mandate");
        } else {
          setSepaStep("deposit");
        }
      } catch (err) {
        setSepaSetupError(
          err?.response?.data?.detail || "Impossible d'initialiser le prélèvement SEPA."
        );
      } finally {
        setSepaLoading(false);
      }
    };
    fetchSepaSetup();
  }, [provider, sepaClientSecret, sepaSetupError, member, isSettled]);

  // Acompte : PaymentIntent carte créé à l'entrée de l'étape « deposit ».
  // `sepaClientSecret` sert de garde « le setup a répondu » — sans quoi on
  // pourrait créer un acompte alors que le setup va rendre deposit_paid.
  useEffect(() => {
    if (
      provider !== "sepa" || sepaStep !== "deposit" || !sepaClientSecret ||
      sepaDepositSecret || sepaSetupError || !member || isSettled
    ) return;
    const fetchDepositIntent = async () => {
      setSepaDepositLoading(true);
      try {
        const token = localStorage.getItem("hafani_member_token");
        const res = await axios.post(
          `${API}/payments/stripe/create-intent`,
          { purpose: "installment_deposit" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSepaDepositSecret(res.data.client_secret);
        setSepaDepositPaymentId(res.data.payment_id);
        setSepaDepositAmount(res.data.amount);
      } catch (err) {
        setSepaSetupError(
          err?.response?.data?.detail || "Impossible d'initialiser l'acompte."
        );
      } finally {
        setSepaDepositLoading(false);
      }
    };
    fetchDepositIntent();
  }, [provider, sepaStep, sepaClientSecret, sepaDepositSecret, sepaSetupError, member, isSettled]);

  const handleSepaDepositSuccess = () => {
    fetchMemberAndBalance();
    setSepaStep("mandate");
  };

  const handleSepaSuccess = (data) => {
    setSepaResult(data);
  };

  const confirmSepaMandate = async (payload) => {
    const token = localStorage.getItem("hafani_member_token");
    const res = await axios.post(
      `${API}/payments/stripe/create-sepa-subscription`,
      { ...payload, deposit_payment_id: sepaDepositPaymentId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.data;
  };

  // PayPal
  const createPaymentRecord = useCallback(async () => {
    if (!member) return null;
    try {
      const token = localStorage.getItem("hafani_member_token");
      const res = await axios.post(
        `${API}/payments/init`,
        { membership_type: member.membership_type || "adhesion", country: member.country },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      return res.data;
    } catch {
      toast({ title: "Erreur", description: "Impossible d'initialiser le paiement.", variant: "destructive" });
      throw new Error("PayPal init failed");
    }
  }, [member, toast]);

  const handlePayPalSuccess = (details) => {
    toast({
      title: "Paiement confirmé",
      description: `Transaction de ${details.purchase_units[0].amount.value} ${details.purchase_units[0].amount.currency_code} validée.`,
    });
    fetchMemberAndBalance();
    navigate("/confirmation");
  };

  const handleStripeSuccess = () => {
    toast({ title: "Paiement confirmé", description: "Votre versement a bien été enregistré." });
    fetchMemberAndBalance();
    navigate("/confirmation");
  };

  const handleStripeError = (error) => {
    toast({ title: "Échec du paiement", description: error.message, variant: "destructive" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EAE8E4] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!member) return null;

  const cotisationAmount = balance?.cotisation_amount ?? member.cotisation_amount ?? 50;
  const paidAmount = balance?.paid_amount ?? 0;
  const recoveryPct = balance ? Math.round(balance.recovery_rate * 100) : 0;

  return (
    <div className="min-h-screen bg-[#EAE8E4] font-sans antialiased flex flex-col">

      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-200/60 bg-[#EAE8E4]/90 backdrop-blur-md">
        <div className="max-w-xl mx-auto flex h-16 items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex flex-col items-start group">
            <img src="/assets/logo.png" alt="Hafani" className="h-9 w-auto object-contain transition-transform group-hover:scale-105" />
            <span className="text-[9px] text-emerald-700 font-medium mt-0.5 tracking-wide">
              Hafani, une solution pensée pour notre fin
            </span>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Déconnexion</span>
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-xl mx-auto w-full px-4 sm:px-6 py-10 lg:py-14 space-y-7">

        {/* Progress steps */}
        <div className="flex items-center gap-1 justify-center">
          {STEPS.map((step, i) => (
            <React.Fragment key={step}>
              <span className={`text-xs font-semibold tracking-wide transition-colors ${
                i === 1 ? "text-slate-900" : i < 1 ? "text-emerald-600" : "text-slate-300"
              }`}>
                {step}
              </span>
              {i < STEPS.length - 1 && (
                <ChevronRight className={`h-3 w-3 flex-shrink-0 ${i < 1 ? "text-emerald-400" : "text-slate-200"}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Title */}
        <div className="text-center">
          <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 leading-tight">
            Régler ma <em className="not-italic text-emerald-700">cotisation.</em>
          </h1>
          <p className="text-slate-400 text-sm font-light mt-3">
            Contribuez à la solidarité de votre communauté Hafani.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl p-7 sm:p-9 shadow-sm border border-slate-100 space-y-7">

          {/* Summary */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-4">Récapitulatif</p>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-500">Membre</span>
                <span className="font-medium text-slate-900">{member.first_name} {member.last_name}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-500">Année</span>
                <span className="font-medium text-slate-900">{balance?.year ?? new Date().getFullYear()}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-500">Cotisation annuelle</span>
                <span className="font-medium text-slate-900">{eur(cotisationAmount)} €</span>
              </div>
              {paidAmount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500">Déjà réglé</span>
                  <span className="font-medium text-emerald-700">{eur(paidAmount)} €</span>
                </div>
              )}

              {/* Barre de progression du recouvrement */}
              {cotisationAmount > 0 && (
                <div className="pt-1">
                  <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${isSettled ? "bg-emerald-500" : "bg-amber-500"}`}
                      style={{ width: `${Math.min(100, recoveryPct)}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="border-t border-slate-100 pt-4 flex justify-between items-baseline">
                <span className="text-sm font-semibold text-slate-900">
                  {isSettled ? "Cotisation réglée" : "Reste à régler"}
                </span>
                <span className={`font-serif text-4xl tracking-tight ${isSettled ? "text-emerald-700" : "text-emerald-700"}`}>
                  {eur(remaining)} €
                </span>
              </div>
            </div>
          </div>

          {isSettled ? (
            /* État soldé — rien à payer */
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-6 text-center space-y-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto" />
              <p className="font-semibold text-emerald-800">
                Votre cotisation {balance?.year ?? ""} est intégralement réglée.
              </p>
              <p className="text-sm text-emerald-700/80">Merci pour votre contribution à la solidarité Hafani.</p>
              <button
                type="button"
                onClick={() => navigate("/mon-dossier")}
                className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3.5 text-sm transition-colors"
              >
                Retour au dossier
              </button>
            </div>
          ) : (
            <>
              {/* Provider selector */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-3">Moyen de paiement</p>
                {(() => {
                  const providers = [
                    { id: "stripe", label: "💳 Carte" },
                    ...(member?.stripe_charges_enabled && remaining >= 50
                      ? [{ id: "sepa", label: "🏦 SEPA mensuel" }]
                      : []),
                    { id: "paypal", label: "PayPal" },
                  ];
                  return (
                    <div className={`grid grid-cols-${providers.length} gap-2 rounded-2xl bg-slate-50 border border-slate-100 p-1`}>
                      {providers.map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setProvider(id)}
                          className={`rounded-xl py-2.5 text-sm font-semibold transition-all ${
                            provider === id
                              ? "bg-white shadow-sm text-slate-900 border border-slate-200"
                              : "text-slate-400 hover:text-slate-600"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Payment zone */}
              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-5">
                {provider === "stripe" ? (
                  cbStep === "choose" ? (
                    <div className="space-y-4">
                      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 text-center">
                        Montant à régler
                      </p>
                      <div className="grid grid-cols-2 gap-2 rounded-2xl bg-white border border-slate-200 p-1">
                        <button
                          type="button"
                          onClick={() => setCbAmountMode("remaining")}
                          className={`rounded-xl py-2.5 text-sm font-semibold transition-all ${
                            cbAmountMode === "remaining"
                              ? "bg-emerald-600 text-white shadow-sm"
                              : "text-slate-500 hover:text-slate-800"
                          }`}
                        >
                          Solde restant ({eur(remaining)} €)
                        </button>
                        <button
                          type="button"
                          onClick={() => setCbAmountMode("custom")}
                          className={`rounded-xl py-2.5 text-sm font-semibold transition-all ${
                            cbAmountMode === "custom"
                              ? "bg-emerald-600 text-white shadow-sm"
                              : "text-slate-500 hover:text-slate-800"
                          }`}
                        >
                          Montant libre
                        </button>
                      </div>

                      {cbAmountMode === "custom" && (
                        <div>
                          <div className="relative">
                            <input
                              type="number"
                              inputMode="decimal"
                              min={customMinAmount}
                              max={remaining}
                              step="0.01"
                              value={cbCustomAmount}
                              onChange={(e) => setCbCustomAmount(e.target.value)}
                              placeholder={eur(remaining)}
                              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">€</span>
                          </div>
                          <p className="mt-1.5 text-xs text-slate-400">
                            Entre {eur(customMinAmount)} € et {eur(remaining)} €.
                          </p>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={handleCbContinue}
                        disabled={
                          stripeLoading ||
                          (cbAmountMode === "custom" &&
                            (!cbCustomAmount ||
                              parseFloat(cbCustomAmount) < customMinAmount ||
                              parseFloat(cbCustomAmount) > remaining))
                        }
                        className="w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3.5 text-sm transition-colors"
                      >
                        {stripeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continuer →"}
                      </button>
                    </div>
                  ) : stripeClientSecret ? (
                    <div className="space-y-3">
                      <Elements key={stripeClientSecret} stripe={stripePromise} options={{ clientSecret: stripeClientSecret }}>
                        <StripePaymentForm
                          clientSecret={stripeClientSecret}
                          onSuccess={handleStripeSuccess}
                          onError={handleStripeError}
                          amount={stripeAmount || remaining}
                        />
                      </Elements>
                      <button
                        type="button"
                        onClick={handleCbChangeAmount}
                        className="w-full text-xs text-slate-400 hover:text-slate-700 transition-colors"
                      >
                        ← Modifier le montant
                      </button>
                    </div>
                  ) : (
                    <p className="text-center text-sm text-slate-400 py-4">Impossible de charger le formulaire de paiement.</p>
                  )
                ) : provider === "sepa" ? (
                  sepaResult ? (
                    <div className="text-center space-y-4 py-4">
                      <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Mandat SEPA enregistré !</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {sepaResult.installments ?? 12} prélèvements de{" "}
                          <strong>
                            {eur(sepaResult.monthly_amount)} €
                          </strong>{" "}
                          par mois, à partir du <strong>{sepaResult.next_payment_date ?? "—"}</strong>.
                        </p>
                        <p className="text-xs text-slate-400 mt-2">
                          Couvre le solde de {eur(sepaResult.total_amount)} € restant après l'acompte. Le prélèvement
                          s'arrête automatiquement après la dernière mensualité — aucune reconduction pour l'exercice suivant.
                        </p>
                      </div>
                      <button
                        onClick={() => navigate("/confirmation")}
                        className="w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3.5 text-sm transition-colors"
                      >
                        Voir ma carte membre →
                      </button>
                    </div>
                  ) : sepaSetupError ? (
                    <p className="text-center text-sm text-slate-500 py-4">{sepaSetupError}</p>
                  ) : sepaLoading ? (
                    <div className="flex justify-center py-6">
                      <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                    </div>
                  ) : sepaStep === "deposit" ? (
                    <div className="space-y-4">
                      <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
                        Acompte de <strong>{eur(sepaDepositAmount)} €</strong> à régler par carte,
                        puis <strong>{sepaMonthlyAmount != null ? `12 × ${eur(sepaMonthlyAmount)} €` : "12 mensualités"}</strong> par prélèvement SEPA.
                      </div>
                      {sepaDepositLoading || !sepaDepositSecret ? (
                        <div className="flex justify-center py-6">
                          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                        </div>
                      ) : (
                        <Elements key={sepaDepositSecret} stripe={stripePromise} options={{ clientSecret: sepaDepositSecret }}>
                          <StripePaymentForm
                            clientSecret={sepaDepositSecret}
                            amount={sepaDepositAmount || 0}
                            onSuccess={handleSepaDepositSuccess}
                            onError={handleStripeError}
                          />
                        </Elements>
                      )}
                    </div>
                  ) : sepaClientSecret ? (
                    <div className="space-y-3">
                      <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-2.5 text-sm text-emerald-800">
                        {sepaDepositWaived
                          ? "Vos versements couvrent déjà l'acompte — autorisez le prélèvement du solde."
                          : "Acompte réglé ✓ — autorisez le prélèvement du solde."}
                      </div>
                      <Elements stripe={stripePromise} options={{ clientSecret: sepaClientSecret }}>
                        <SepaPaymentForm
                          clientSecret={sepaClientSecret}
                          monthlyAmount={sepaMonthlyAmount || remaining / 12}
                          remainingAmount={sepaRemainingAmount ?? remaining}
                          member={member}
                          onConfirmMandate={confirmSepaMandate}
                          onSuccess={handleSepaSuccess}
                          onError={(error) => toast({ title: "Erreur", description: error.message, variant: "destructive" })}
                        />
                      </Elements>
                    </div>
                  ) : (
                    <p className="text-center text-sm text-slate-400 py-4">Impossible de charger le formulaire SEPA.</p>
                  )
                ) : (
                  <>
                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4 text-center">
                      Payer en sécurité avec PayPal
                    </p>
                    <div className="relative z-0">
                      <PayPalButton
                        amount={remaining}
                        currency="EUR"
                        createPaymentRecord={createPaymentRecord}
                        onSuccess={handlePayPalSuccess}
                      />
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* Cancel */}
          <button
            type="button"
            className="w-full text-sm text-slate-400 hover:text-slate-700 transition-colors py-1"
            onClick={() => navigate("/mon-dossier")}
          >
            ← Retour au dossier
          </button>
        </div>

      </main>

      <footer className="py-6 text-center border-t border-slate-200/60">
        <p className="text-xs text-slate-400 font-light">
          &copy; {new Date().getFullYear()} Hafani Management and Solutions
        </p>
      </footer>

    </div>
  );
};
